import { useState, useEffect, useCallback } from "react";
import { UserCog, LogIn, Ban, CheckCircle2, Trash2, Save, ArrowDownLeft, ArrowUpRight, KeyRound } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { timeAgo, clsx, usd } from "../lib/format";
import { COUNTRIES } from "../lib/countries";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

const KYC = ["not_started", "under_review", "approved", "rejected", "resubmit_required"];
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
const selCls = "w-full h-9 rounded-md bg-fx-base border border-white/10 px-3 text-sm";

function Stat({ label, value, testid }) {
  return <div className="rounded-lg bg-fx-base border border-white/5 p-3" data-testid={testid}><div className="text-[10px] uppercase text-fx-s3">{label}</div><div className="font-mono font-bold text-sm">{value}</div></div>;
}

function Overview({ d }) {
  const live = d.wallets.find((w) => w.mode === "live")?.balance || 0;
  const demo = d.wallets.find((w) => w.mode === "demo")?.balance || 0;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Live wallet" value={usd(live)} testid="cust-live-balance" />
        <Stat label="Demo wallet" value={usd(demo)} />
        <Stat label="Recent trades" value={d.trades.length} />
        <Stat label="KYC" value={d.user.kyc_status?.replace(/_/g, " ")} />
      </div>
      {d.user.status === "suspended" && <div className="rounded-lg border border-neg/30 bg-neg/10 p-3 text-sm" data-testid="cust-suspension-reason"><span className="text-neg font-semibold">Suspended</span>{d.user.suspension_reason ? ` · ${d.user.suspension_reason}` : ""}</div>}
      <div className="grid md:grid-cols-2 gap-4">
        <div><h4 className="text-xs uppercase tracking-wider text-fx-s3 mb-2">Recent Deposits</h4>
          {d.deposits.length === 0 ? <p className="text-fx-s3 text-sm">None</p> : d.deposits.slice(0, 5).map((x) => <div key={x.id} className="flex justify-between text-sm py-1"><span className="text-fx-s3">{x.provider}</span><span className="font-mono text-pos">+{usd(x.amount_usd)}</span></div>)}
        </div>
        <div><h4 className="text-xs uppercase tracking-wider text-fx-s3 mb-2">Recent Trades</h4>
          {d.trades.length === 0 ? <p className="text-fx-s3 text-sm">None</p> : d.trades.slice(0, 5).map((t) => <div key={t.id} className="flex justify-between text-sm py-1"><span className="text-fx-s3">{t.asset} · {t.direction}</span><span className={clsx("font-mono", t.result === "win" ? "text-pos" : t.result === "loss" ? "text-neg" : "text-fx-s3")}>{t.result || "open"}</span></div>)}
        </div>
      </div>
    </div>
  );
}

function ProfileForm({ u, onSaved }) {
  const [f, setF] = useState({ name: u.name || "", email: u.email || "", phone: u.phone || "", country: u.country || "", kyc_status: u.kyc_status || "not_started" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/admin/customers/${u.id}`, f); toast.success("Profile updated"); onSaved(); } catch (e) { err(e); }
    setBusy(false);
  };
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div className="space-y-1"><Label className="text-xs">Full name</Label><Input data-testid="cust-edit-name" value={f.name} onChange={set("name")} className="bg-fx-base border-white/10" /></div>
      <div className="space-y-1"><Label className="text-xs">Email</Label><Input data-testid="cust-edit-email" value={f.email} onChange={set("email")} className="bg-fx-base border-white/10" /></div>
      <div className="space-y-1"><Label className="text-xs">Phone</Label><Input data-testid="cust-edit-phone" value={f.phone} onChange={set("phone")} className="bg-fx-base border-white/10" /></div>
      <div className="space-y-1"><Label className="text-xs">Country</Label>
        <select data-testid="cust-edit-country" value={f.country} onChange={set("country")} className={selCls}><option value="">Select country</option>{COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}</select>
      </div>
      <div className="space-y-1"><Label className="text-xs">KYC status</Label>
        <select data-testid="cust-edit-kyc" value={f.kyc_status} onChange={set("kyc_status")} className={selCls}>{KYC.map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}</select>
      </div>
      <div className="sm:col-span-2 flex justify-end"><Button onClick={save} disabled={busy} data-testid="cust-edit-save" className="bg-brandblue hover:bg-blue-500 gap-2"><Save className="h-4 w-4" /> Save changes</Button></div>
    </div>
  );
}

function WalletPanel({ u, live, onChanged }) {
  const [type, setType] = useState("credit");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/admin/customers/${u.id}/wallet/history`).then((r) => setRows(r.data)), [u.id]);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    if (!(Number(amount) > 0)) return toast.error("Enter a valid amount");
    if (reason.trim().length < 3) return toast.error("A reason is required");
    if (!window.confirm(`${type === "credit" ? "Credit" : "Debit"} ${usd(Number(amount))} ${type === "credit" ? "to" : "from"} ${u.email}'s live wallet?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/customers/${u.id}/wallet/adjust`, { type, amount: Number(amount), reason });
      toast.success("Wallet adjusted"); setAmount(""); setReason(""); load(); onChanged();
    } catch (e) { err(e); }
    setBusy(false);
  };
  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-fx-base border border-white/5 p-4 space-y-3">
        <div className="flex items-center justify-between"><span className="text-xs uppercase text-fx-s3">Live balance</span><span className="font-mono font-bold" data-testid="cust-wallet-live">{usd(live)}</span></div>
        <div className="flex gap-2">
          {["credit", "debit"].map((t) => (
            <button key={t} onClick={() => setType(t)} data-testid={`cust-adjust-type-${t}`} className={clsx("flex-1 h-9 rounded-md text-sm font-semibold border transition-colors", type === t ? (t === "credit" ? "bg-pos/20 border-pos text-pos" : "bg-neg/20 border-neg text-neg") : "border-white/10 text-fx-s3")}>{t === "credit" ? "Credit (+)" : "Debit (−)"}</button>
          ))}
        </div>
        <Input type="number" min="0" step="0.01" placeholder="Amount (USD)" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cust-adjust-amount" className="bg-fx-s1 border-white/10 font-mono" />
        <Textarea placeholder="Reason (required, saved in the audit log)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cust-adjust-reason" className="bg-fx-s1 border-white/10 min-h-[60px]" />
        <Button onClick={submit} disabled={busy} data-testid="cust-adjust-submit" className="w-full bg-brandblue hover:bg-blue-500">Apply adjustment</Button>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-wider text-fx-s3 mb-2">Live wallet history</h4>
        <div className="max-h-64 overflow-y-auto divide-y divide-white/5" data-testid="cust-wallet-history">
          {rows.length === 0 && <p className="text-fx-s3 text-sm py-2">No transactions yet.</p>}
          {rows.map((t) => (
            <div key={t.id} className="flex items-start justify-between gap-3 py-2 text-sm" data-testid={`cust-tx-${t.id}`}>
              <div className="flex items-start gap-2 min-w-0">
                {t.amount >= 0 ? <ArrowDownLeft className="h-4 w-4 text-pos mt-0.5 shrink-0" /> : <ArrowUpRight className="h-4 w-4 text-neg mt-0.5 shrink-0" />}
                <div className="min-w-0">
                  <div className="font-medium">{t.type.replace(/_/g, " ")}</div>
                  {t.reason && <div className="text-xs text-fx-s3 truncate">{t.reason}{t.admin_email ? ` · by ${t.admin_email}` : ""}</div>}
                  <div className="text-[10px] text-fx-s3 font-mono">{timeAgo(t.created_at)}</div>
                </div>
              </div>
              <span className={clsx("font-mono shrink-0", t.amount >= 0 ? "text-pos" : "text-neg")}>{t.amount >= 0 ? "+" : ""}{usd(t.amount)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SecurityPanel({ u }) {
  const [pw, setPw] = useState("");
  const reset = async () => {
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (!window.confirm(`Set a new password for ${u.email}? They will be signed out everywhere.`)) return;
    try { await api.post(`/admin/customers/${u.id}/password`, { password: pw }); toast.success("Password reset, user signed out"); setPw(""); } catch (e) { err(e); }
  };
  return (
    <div className="space-y-3">
      <Label className="text-xs">New password</Label>
      <div className="flex gap-2">
        <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" data-testid="cust-reset-password-input" className="bg-fx-base border-white/10 font-mono" />
        <Button onClick={reset} data-testid="cust-reset-password-btn" className="bg-brandblue hover:bg-blue-500 gap-2 shrink-0"><KeyRound className="h-4 w-4" /> Reset</Button>
      </div>
      <p className="text-xs text-fx-s3">The user will be logged out of every device and must sign in with this new password. Share it securely.</p>
    </div>
  );
}

function SuspendDialog({ u, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const go = async () => {
    try { await api.patch(`/admin/customers/${u.id}`, { status: "suspended", suspension_reason: reason }); toast.success("Customer suspended"); onDone(); onClose(); } catch (e) { err(e); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-fx-s2 border-white/10 text-white" data-testid="cust-suspend-dialog">
        <DialogHeader><DialogTitle className="font-display">Suspend {u.email}</DialogTitle></DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (shown to the user when they try to log in)" data-testid="cust-suspend-reason" className="bg-fx-base border-white/10" />
        <DialogFooter><Button onClick={go} data-testid="cust-suspend-confirm" className="bg-neg hover:bg-red-600">Suspend account</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCustomerDetail({ detail, can, onClose, onRefresh, onImpersonate, onDelete }) {
  const [suspending, setSuspending] = useState(false);
  if (!detail) return null;
  const u = detail.user;
  const live = detail.wallets.find((w) => w.mode === "live")?.balance || 0;
  const reactivate = async () => {
    try { await api.patch(`/admin/customers/${u.id}`, { status: "active" }); toast.success("Customer reactivated"); onRefresh(); } catch (e) { err(e); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-fx-s2 border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cust-detail-dialog">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><UserCog className="h-5 w-5 text-brandblue" /> {u.name}</DialogTitle></DialogHeader>
        <div className="text-xs text-fx-s3 font-mono -mt-2">{u.email}{u.phone ? ` · ${u.phone}` : ""}{u.country ? ` · ${u.country}` : ""}</div>
        <Tabs defaultValue="overview">
          <TabsList className="bg-fx-base border border-white/5">
            <TabsTrigger value="overview" data-testid="cust-tab-overview" className="data-[state=active]:bg-brandblue">Overview</TabsTrigger>
            {can("users.edit") && <TabsTrigger value="profile" data-testid="cust-tab-profile" className="data-[state=active]:bg-brandblue">Profile</TabsTrigger>}
            <TabsTrigger value="wallet" data-testid="cust-tab-wallet" className="data-[state=active]:bg-brandblue">Wallet</TabsTrigger>
            {can("users.edit") && <TabsTrigger value="security" data-testid="cust-tab-security" className="data-[state=active]:bg-brandblue">Security</TabsTrigger>}
          </TabsList>
          <TabsContent value="overview" className="mt-4"><Overview d={detail} /></TabsContent>
          {can("users.edit") && <TabsContent value="profile" className="mt-4"><ProfileForm key={u.id + u.email} u={u} onSaved={onRefresh} /></TabsContent>}
          <TabsContent value="wallet" className="mt-4">{can("users.edit") ? <WalletPanel u={u} live={live} onChanged={onRefresh} /> : <Stat label="Live wallet" value={usd(live)} />}</TabsContent>
          {can("users.edit") && <TabsContent value="security" className="mt-4"><SecurityPanel u={u} /></TabsContent>}
        </Tabs>
        <DialogFooter className="flex-wrap gap-2 pt-2 border-t border-white/5">
          {can("users.impersonate") && <Button onClick={() => onImpersonate(u.id)} data-testid="cust-detail-impersonate" className="bg-brandblue hover:bg-blue-500 gap-1"><LogIn className="h-4 w-4" /> Login as customer</Button>}
          {can("users.edit") && (u.status === "suspended"
            ? <Button onClick={reactivate} data-testid="cust-toggle-status" variant="outline" className="border-white/10 bg-fx-base gap-1 text-pos"><CheckCircle2 className="h-4 w-4" /> Reactivate</Button>
            : <Button onClick={() => setSuspending(true)} data-testid="cust-toggle-status" variant="outline" className="border-white/10 bg-fx-base gap-1 text-gold"><Ban className="h-4 w-4" /> Suspend</Button>)}
          {can("users.delete") && <Button onClick={() => onDelete(u)} data-testid="cust-delete-btn" variant="ghost" className="text-neg gap-1"><Trash2 className="h-4 w-4" /> Delete</Button>}
        </DialogFooter>
        {suspending && <SuspendDialog u={u} onClose={() => setSuspending(false)} onDone={onRefresh} />}
      </DialogContent>
    </Dialog>
  );
}
